import React from 'react';
import ReactECharts from 'echarts-for-react';
import { DividendHistroyData } from '../../../types/dividendhistory.type';
import { getCssVariable, getAxisTickConfig } from '../../../utils/utils';
import { ThemeType } from '../../../utils/type';
import { formatLocaleNumber } from '../../../helpers/number';

interface DividendChartProps {
  data: DividendHistroyData;
  theme?: ThemeType;
}

const DividendChart: React.FC<DividendChartProps> = ({ data, theme }) => {
  if (!data?.bars || data.bars.length === 0) return null;

  const bars = [...(data?.bars ?? [])].reverse();
  const years = bars.map((b) =>
    new Date(b.period_end_date).getFullYear().toString(),
  );
  const values = bars.map((b) => +formatLocaleNumber(b.dividend_ann, 2));

  const { max: yMax, interval: yInterval } = getAxisTickConfig(values, 5);

  const barChartOption = {
    textStyle: {
      fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
    },
    grid: { left: 8, right: 0, top: 16, bottom: 40, containLabel: true },
    legend: {
      data: ['Dividends per share'],
      bottom: 0,
      itemWidth: 10,
      itemHeight: 10,
      icon: 'rect',
      textStyle: {
        color: getCssVariable('--text-primary'),
        fontSize: 12,
      },
    },
    tooltip: {
      position: 'top',
      extraCssText: 'padding: 6px; min-width:120px',
      formatter: (params: any) => {
        const { name, value, marker, seriesName } = Array.isArray(params)
          ? params[0]
          : params;
        const formattedValue =
          `${formatLocaleNumber(value, 2)} ${data?.ratio?.div_currency ?? ''}`.trim();
        return `
          <div style="min-width:120px;">
            <div>${seriesName ?? ''}</div>
            <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;">
              <div>${marker}<span>${name}</span></div>
              <strong>${formattedValue}</strong>
            </div>
          </div>
        `;
      },
      textStyle: {
        fontSize: 12,
      },
    },
    xAxis: {
      type: 'category',
      data: years,
      axisTick: { show: false },
      axisLine: {
        lineStyle: { color: getCssVariable('--border-line'), opacity: 0.6 },
      },
      axisLabel: { color: getCssVariable('--text-secondary'), fontSize: 14 },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      min: 0,
      max: yMax,
      interval: yInterval,
      splitLine: {
        lineStyle: {
          color: getCssVariable('--border-tertiary'),
          opacity: 0.6,
        },
      },
      axisLabel: {
        color: getCssVariable('--text-secondary'),
        fontSize: 14,
        formatter: (v: number) => v.toFixed(2),
      },
    },
    series: [
      {
        name: 'Dividends per share',
        type: 'bar',
        data: values,
        barWidth: '40%',
        barMaxWidth: 100,
        barMinWidth: 30,
        itemStyle: { color: getCssVariable('--chart-primary', theme) },
      },
    ],
  };

  return (
    <div style={{ padding: '16px 32px 0' }}>
      <ReactECharts
        option={barChartOption}
        style={{ width: '100%', height: 180 }}
      />
    </div>
  );
};

export default DividendChart;
